import { useCallback, useState } from "react";
import useSWR from "swr";

import {
  createProduct,
  deleteProduct,
  listProducts,
  PRODUCTS_KEY,
  updateProduct,
  type CreateProductPayload,
  type UpdateProductPayload,
} from "@/features/products/api/productsApi";
import type { ProductCatalogItem } from "@/features/products/types";
import { revalidate, toErrorMessage } from "@/lib/swr";

const EMPTY: ProductCatalogItem[] = [];

/** Deleting a product takes its recipe and supplyings down, so stocks move too. */
const TOUCHED_CACHES = [PRODUCTS_KEY, "recipes", "product-stocks/", "provision-stocks/", "vending"];

export function useProducts() {
  const { data, error, isLoading, mutate } = useSWR(PRODUCTS_KEY, listProducts);

  return {
    products: data ?? EMPTY,
    isLoading,
    error: toErrorMessage(error),
    refresh: mutate,
  };
}

export function useCreateProduct() {
  const { mutate } = useSWR(PRODUCTS_KEY, listProducts);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (payload: CreateProductPayload) => {
      setIsCreating(true);
      setError(null);
      try {
        const created = await createProduct(payload);
        await mutate();
        return created;
      } catch (e) {
        setError(toErrorMessage(e));
        throw e;
      } finally {
        setIsCreating(false);
      }
    },
    [mutate],
  );

  return { create, isCreating, error };
}

export function useUpdateProduct() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(async (id: string, payload: UpdateProductPayload) => {
    setIsUpdating(true);
    setError(null);
    try {
      const updated = await updateProduct(id, payload);
      await revalidate(...TOUCHED_CACHES);
      return updated;
    } catch (e) {
      setError(toErrorMessage(e));
      throw e;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  return { update, isUpdating, error };
}

export function useDeleteProduct() {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = useCallback(async (id: string) => {
    setIsDeleting(true);
    setError(null);
    try {
      const deleted = await deleteProduct(id);
      await revalidate(...TOUCHED_CACHES);
      return deleted;
    } catch (e) {
      setError(toErrorMessage(e));
      throw e;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return { remove, isDeleting, error };
}
